// ==========================================
// Layout principal
// ==========================================

import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import LinkAccountsModal from './LinkAccountsModal';
import { SeasonalThemeWrapper } from './SeasonalEvent';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import { useLinkAccountsStore } from '../stores/linkAccountsStore';

export default function Layout() {
  const { user, token, isAuthenticated } = useAuthStore();
  const { 
    showModal, 
    linkedGames, 
    benefits, 
    checkLinkedAccounts, 
    linkAccount, 
    closeModal 
  } = useLinkAccountsStore();
  
  // Vérifier les comptes liés après connexion
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      checkLinkedAccounts(user.email);
    }
  }, [isAuthenticated, user?.email, checkLinkedAccounts]);
  
  const navigate = useNavigate();
  const [loginPrompt, setLoginPrompt] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const msg = (e as CustomEvent).detail?.message || 'Connecte-toi pour accéder à cette fonctionnalité !';
      setLoginPrompt(msg);
    };
    window.addEventListener('open:loginRequired', handler);
    return () => window.removeEventListener('open:loginRequired', handler);
  }, []);

  const handleLinkAccount = async (gameId: string): Promise<boolean> => {
    if (!token) return false;
    return linkAccount(gameId, token);
  };

  return (
    <SeasonalThemeWrapper>
      <div className="min-h-screen bg-arcade-dark bg-arcade-grid">
        <Navbar />
        
        <main className="pt-16">
          <Outlet />
        </main>
        
        {/* Modal de liaison des comptes */}
        <LinkAccountsModal
          isOpen={showModal}
          onClose={closeModal}
          linkedGames={linkedGames}
          benefits={benefits}
          onLinkAccount={handleLinkAccount}
        />
        
        {/* Login required modal */}
        <AnimatePresence>
          {loginPrompt && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
              onClick={() => setLoginPrompt(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="w-full max-w-sm p-6 rounded-2xl text-center"
                style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)' }}
                onClick={e => e.stopPropagation()}
              >
                <div className="text-4xl mb-3">🔒</div>
                <h2 className="font-arcade text-base text-white mb-2">Connexion requise</h2>
                <p className="text-sm text-gray-400 mb-5">{loginPrompt}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setLoginPrompt(null)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-mono transition-all"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#888' }}
                  >
                    Fermer
                  </button>
                  <button
                    onClick={() => { setLoginPrompt(null); navigate('/login'); }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-mono font-bold transition-all"
                    style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.25)', color: '#00D4FF' }}
                  >
                    Se connecter
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#12121a',
              color: '#e0e0e0',
              border: '1px solid #2a2a3a',
            },
            success: {
              iconTheme: {
                primary: '#39ff14',
                secondary: '#12121a',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff4444',
                secondary: '#12121a',
              },
            },
          }}
        />
      </div>
    </SeasonalThemeWrapper>
  );
}
